import React, { useState, useEffect, useRef, useCallback } from "react";
import logo from "../../../assets/images/coinlogo.png";
import cancelbtn from "../../../assets/Task/cancelicon1.png";
import questionMarkIcon from "../../../assets/Task/ReferImg.png";
import "../snake/snake.css";
import useUserInfo from "../../../Hooks/useUserInfo";
import { userGameRewards } from "../../../apis/user";
import { useAudio } from "../../../Providers/AudioProvider";
import task from "../../Task/Task";
import gameleader from "../leaderboard/gameleader";
import { logPageView } from "../../../components/analytics";

const SnakeGame = () => {
  // Canvas references
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  const { userDetails, updateUserInfo, updatewatchScreenInfo, watchScreen } =
    useUserInfo();
  const IsPointsReached =
    watchScreen?.LoginMessage ==
    "You have already reached the maximum rewards limit"
      ? true
      : false;
  // Game dimensions
  const canvasSizeRef = useRef({ width: 0, height: 0 });
  const gridSize = 15;
  const playClick = useAudio();
  const closePopUp = () => {
    // Reset game state
    snakeRef.current = [
      { x: 3 * gridSize, y: 10 * gridSize },
      { x: 2 * gridSize, y: 10 * gridSize },
      { x: 1 * gridSize, y: 10 * gridSize },
    ];
    directionRef.current = "right";
    nextDirectionRef.current = "right";
    setCurrentDirection("right");
    gameSpeedRef.current = 250;
    scoreRef.current = 0;
    setScore(0);
    // Place initial food
    placeNewFood();
    // Hide popup
    setGameOver(false);
    // Don't auto-start the game
    isPlayingRef.current = false;
    setIsPlaying(false);
    // Force initial draw to show starting position
    drawGame();
    // Clear any existing game loop
    if (gameLoopRef.current) {
      clearInterval(gameLoopRef.current);
      gameLoopRef.current = null;
    }
  };

  useEffect(() => {
    logPageView("Snake ", "Snake game");
  }, []);

  const goToThePage = (component, name) => {
    updateUserInfo((prev) => ({
      ...prev,
      currentComponent: component,
      currentComponentText: name,
      lastComponent: userDetails.currentComponent,
      lastComponentText: userDetails.currentComponentText,
      centerCount: userDetails.centerCount + 1,
      isMenu: false,
    }));
  };

  //   const autoStartTimer = setTimeout(() => {
  //     startGame();
  //   }, 500);

  // Game state
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentDirection, setCurrentDirection] = useState("right");
  const [soundEnabled, setSoundEnabled] = useState(true);

  // Snake customization
  const [snakeColor, setSnakeColor] = useState("#0cbd1b"); // Green for better contrast on dark
  const [snakeBodyColor, setSnakeBodyColor] = useState("#0cbd1b");

  // Refs for game state
  const snakeRef = useRef([
    { x: 3 * gridSize, y: 10 * gridSize },
    { x: 2 * gridSize, y: 10 * gridSize },
    { x: 1 * gridSize, y: 10 * gridSize },
  ]);
  const directionRef = useRef("right");
  const nextDirectionRef = useRef("right");
  const foodRef = useRef({ x: 0, y: 0 });
  const gameSpeedRef = useRef(250);
  const scoreRef = useRef(0);
  const gameLoopRef = useRef(null);
  const isPlayingRef = useRef(false);
  const soundEnabledRef = useRef(true);

  // Touch handling for mobile
  const touchStartRef = useRef({ x: 0, y: 0 });
  const touchThreshold = 30;

  // Meme images for food
  const memeImagesRef = useRef([]);
  const currentMemeRef = useRef(null);

  // Default meme placeholder images
  const defaultMemes = [
    "https://d1ccopf5cw1hft.cloudfront.net/s3data/trump.png",
    "https://d1ccopf5cw1hft.cloudfront.net/s3data/pepe.png",
    "https://d1ccopf5cw1hft.cloudfront.net/s3data/bonk.png",
    "https://d1ccopf5cw1hft.cloudfront.net/s3data/floki.png",
    "https://d1ccopf5cw1hft.cloudfront.net/s3data/wif.png",
  ];
  const scrollableRef = useRef(null);

  useEffect(() => {
    const preventTouchScroll = (event) => {
      // Allow scrolling only for the scrollable div
      if (!scrollableRef.current.contains(event.target)) {
        event.preventDefault();
      }
    };
    // Add the event listener
    document.addEventListener("touchmove", preventTouchScroll, {
      passive: false,
    });
    // Cleanup the event listener
    return () => {
      document.removeEventListener("touchmove", preventTouchScroll);
    };
  }, []);

  // Load meme images
  useEffect(() => {
    const images = [];
    defaultMemes.forEach((src) => {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        images.push(img);
        if (images.length === defaultMemes.length) {
          memeImagesRef.current = images;
          getRandomMeme();
        }
      };
    });
  }, []);

  // Update sound enabled ref when state changes
  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  // Get random meme image
  const getRandomMeme = useCallback(() => {
    if (memeImagesRef.current.length === 0) return;

    const index = Math.floor(Math.random() * memeImagesRef.current.length);
    currentMemeRef.current = memeImagesRef.current[index];
  }, []);

  // Play sound effect
  const playSound = useCallback((type) => {
    if (!soundEnabledRef.current) return;

    try {
      const context = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = context.createOscillator();
      const gain = context.createGain();

      oscillator.connect(gain);
      gain.connect(context.destination);

      switch (type) {
        case "eat":
          oscillator.type = "sine";
          oscillator.frequency.setValueAtTime(880, context.currentTime);
          oscillator.frequency.exponentialRampToValueAtTime(
            440,
            context.currentTime + 0.1
          );
          gain.gain.setValueAtTime(0.5, context.currentTime);
          gain.gain.exponentialRampToValueAtTime(
            0.01,
            context.currentTime + 0.1
          );
          oscillator.start();
          oscillator.stop(context.currentTime + 0.1);
          break;

        case "move":
          oscillator.type = "sine";
          oscillator.frequency.setValueAtTime(220, context.currentTime);
          gain.gain.setValueAtTime(0.2, context.currentTime);
          gain.gain.exponentialRampToValueAtTime(
            0.01,
            context.currentTime + 0.05
          );
          oscillator.start();
          oscillator.stop(context.currentTime + 0.05);
          break;

        case "gameover":
          oscillator.type = "sawtooth";
          oscillator.frequency.setValueAtTime(200, context.currentTime);
          oscillator.frequency.exponentialRampToValueAtTime(
            50,
            context.currentTime + 0.3
          );
          gain.gain.setValueAtTime(0.5, context.currentTime);
          gain.gain.exponentialRampToValueAtTime(
            0.01,
            context.currentTime + 0.3
          );
          oscillator.start();
          oscillator.stop(context.currentTime + 0.3);
          break;

        default:
          break;
      }
    } catch (e) {
      console.error("Audio error:", e);
    }
  }, []);

  // Vibrate device if supported
  const vibrateDevice = useCallback((duration = 20) => {
    if (navigator.vibrate) {
      navigator.vibrate(duration);
    }
  }, []);

  const handleApiResponse = async (points) => {
    updateUserInfo((prev) => ({
      ...prev,
      isLoading: true,
    }));
    try {
      const apiData = {
        gamePoints: points,
        gameType: "snake",
      };
      const res = await userGameRewards(apiData); // Replace with your actual API call
      if (res) {
        updateUserInfo((prev) => ({
          ...prev,
          isLoading: false,
        }));
        updatewatchScreenInfo((prev) => ({
          ...prev,
          LoginMessage: res?.message,
        }));
        setGameOver(true);
      }
    } catch (error) {
      console.error("API call failed:", error);
    }
  };
  // Game over handler
  const handleGameOver = useCallback(() => {
    isPlayingRef.current = false;
    setIsPlaying(false);
    setScore((prevScore) => {
      handleApiResponse(prevScore); // Use updated score
      return prevScore;
    });

    // Play game over sound
    playSound("gameover");

    // Long vibration for game over
    vibrateDevice(100);

    if (gameLoopRef.current) {
      clearInterval(gameLoopRef.current);
      gameLoopRef.current = null;
    }
  }, [playSound, vibrateDevice]);

  // Draw game elements
  const drawGame = useCallback(() => {
    if (!contextRef.current || !canvasRef.current) return;
    const ctx = contextRef.current;
    const canvas = canvasRef.current;

    // Clear canvas with dark background
    ctx.fillStyle = "#121212";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw grid lines
    ctx.strokeStyle = "#222222";
    ctx.lineWidth = 0.5;
    const { width, height } = canvasSizeRef.current;
    for (let x = 0; x < width; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
    for (let y = 0; y < height; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }
    // Draw snake
    snakeRef.current.forEach((segment, index) => {
      // Draw segments with different colors for head and body
      ctx.fillStyle = index === 0 ? snakeColor : snakeBodyColor;
      ctx.fillRect(segment.x, segment.y, gridSize, gridSize);
      ctx.strokeStyle = "#1a1a1a";
      ctx.strokeRect(segment.x, segment.y, gridSize, gridSize);

      // Add eyes to head
      if (index === 0) {
        const eyeSize = gridSize / 5;
        ctx.fillStyle = "#000000";

        if (directionRef.current === "right") {
          ctx.fillRect(
            segment.x + gridSize - eyeSize * 2,
            segment.y + eyeSize,
            eyeSize,
            eyeSize
          );
          ctx.fillRect(
            segment.x + gridSize - eyeSize * 2,
            segment.y + gridSize - eyeSize * 2,
            eyeSize,
            eyeSize
          );
        } else if (directionRef.current === "left") {
          ctx.fillRect(
            segment.x + eyeSize,
            segment.y + eyeSize,
            eyeSize,
            eyeSize
          );
          ctx.fillRect(
            segment.x + eyeSize,
            segment.y + gridSize - eyeSize * 2,
            eyeSize,
            eyeSize
          );
        } else if (directionRef.current === "up") {
          ctx.fillRect(
            segment.x + eyeSize,
            segment.y + eyeSize,
            eyeSize,
            eyeSize
          );
          ctx.fillRect(
            segment.x + gridSize - eyeSize * 2,
            segment.y + eyeSize,
            eyeSize,
            eyeSize
          );
        } else if (directionRef.current === "down") {
          ctx.fillRect(
            segment.x + eyeSize,
            segment.y + gridSize - eyeSize * 2,
            eyeSize,
            eyeSize
          );
          ctx.fillRect(
            segment.x + gridSize - eyeSize * 2,
            segment.y + gridSize - eyeSize * 2,
            eyeSize,
            eyeSize
          );
        }
      }
    });

    // Draw food (meme image)
    if (currentMemeRef.current) {
      // Increase size of food image by 1.5x
      const foodSize = gridSize * 2;
      const offsetX = (foodSize - gridSize) / 2;
      const offsetY = (foodSize - gridSize) / 2;

      // Draw meme image centered and larger
      ctx.drawImage(
        currentMemeRef.current,
        foodRef.current.x - offsetX,
        foodRef.current.y - offsetY,
        foodSize,
        foodSize
      );
    } else {
      // Fallback if meme not loaded
      ctx.beginPath();
      const radius = gridSize * 0.75; // Larger circle for fallback
      ctx.arc(
        foodRef.current.x + gridSize / 2,
        foodRef.current.y + gridSize / 2,
        radius,
        0,
        Math.PI * 2
      );
      ctx.fillStyle = "#f43f5e";
      ctx.fill();
      ctx.strokeStyle = "#ffffff";
      ctx.stroke();
    }
  }, [snakeColor, snakeBodyColor, gameOver]);

  // Place food at random position
  const placeNewFood = useCallback(() => {
    if (!canvasRef.current) return;

    const { width, height } = canvasSizeRef.current;
    const maxX = Math.floor(width / gridSize) - 1;
    const maxY = Math.floor(height / gridSize) - 1;

    let foodX, foodY, foodOnSnake;

    do {
      foodX = Math.floor(Math.random() * maxX) * gridSize;
      foodY = Math.floor(Math.random() * maxY) * gridSize;

      // Check if food is on snake
      foodOnSnake = snakeRef.current.some(
        (segment) => segment.x === foodX && segment.y === foodY
      );
    } while (foodOnSnake);

    foodRef.current = { x: foodX, y: foodY };

    // Get a new random meme image
    getRandomMeme();
  }, [getRandomMeme]);

  // Resize canvas function
  const resizeCanvas = useCallback(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const container = canvas.parentElement;
    const containerWidth = container.clientWidth;

    // Make canvas square and ensure grid alignment
    // Increase canvas size by using a multiplier (1.2 = 20% larger)
    const sizeMultiplier = 1.2;
    const canvasSize =
      Math.floor((containerWidth * sizeMultiplier) / gridSize) * gridSize;
    canvas.width = canvasSize;
    canvas.height = canvasSize;

    canvasSizeRef.current = { width: canvasSize, height: canvasSize };

    if (contextRef.current) {
      drawGame();
    }
  }, [drawGame]);

  // Handle touch start for mobile swipe controls
  const handleTouchStart = useCallback((e) => {
    if (!isPlayingRef.current) return;

    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };

    e.preventDefault();
  }, []);

  // Handle touch end for mobile swipe controls
  const handleTouchEnd = useCallback(
    (e) => {
      if (!isPlayingRef.current) return;

      const touchEnd = {
        x: e.changedTouches[0].clientX,
        y: e.changedTouches[0].clientY,
      };

      const dx = touchEnd.x - touchStartRef.current.x;
      const dy = touchEnd.y - touchStartRef.current.y;

      // Check if swipe distance meets minimum threshold
      if (Math.abs(dx) < touchThreshold && Math.abs(dy) < touchThreshold) {
        return; // Not a swipe, just a tap
      }

      // Determine swipe direction
      if (Math.abs(dx) > Math.abs(dy)) {
        // Horizontal swipe
        if (dx > 0) {
          // Right swipe
          if (directionRef.current !== "left") {
            nextDirectionRef.current = "right";
            setCurrentDirection("right");
            vibrateDevice();
            playSound("move");
          }
        } else {
          // Left swipe
          if (directionRef.current !== "right") {
            nextDirectionRef.current = "left";
            setCurrentDirection("left");
            vibrateDevice();
            playSound("move");
          }
        }
      } else {
        // Vertical swipe
        if (dy > 0) {
          // Down swipe
          if (directionRef.current !== "up") {
            nextDirectionRef.current = "down";
            setCurrentDirection("down");
            vibrateDevice();
            playSound("move");
          }
        } else {
          // Up swipe
          if (directionRef.current !== "down") {
            nextDirectionRef.current = "up";
            setCurrentDirection("up");
            vibrateDevice();
            playSound("move");
          }
        }
      }

      e.preventDefault();
    },
    [vibrateDevice, playSound]
  );

  // Prevent default on touch move to avoid scrolling
  const handleTouchMove = useCallback((e) => {
    if (isPlayingRef.current) {
      e.preventDefault();
    }
  }, []);

  // Handle key controls
  const handleKeyDown = useCallback(
    (e) => {
      if (!isPlayingRef.current) return;

      switch (e.key) {
        case "ArrowUp":
        case "w":
        case "W":
          if (directionRef.current !== "down") {
            nextDirectionRef.current = "up";
            setCurrentDirection("up");
            playSound("move");
          }
          e.preventDefault();
          break;
        case "ArrowDown":
        case "s":
        case "S":
          if (directionRef.current !== "up") {
            nextDirectionRef.current = "down";
            setCurrentDirection("down");
            playSound("move");
          }
          e.preventDefault();
          break;
        case "ArrowLeft":
        case "a":
        case "A":
          if (directionRef.current !== "right") {
            nextDirectionRef.current = "left";
            setCurrentDirection("left");
            playSound("move");
          }
          e.preventDefault();
          break;
        case "ArrowRight":
        case "d":
        case "D":
          if (directionRef.current !== "left") {
            nextDirectionRef.current = "right";
            setCurrentDirection("right");
            playSound("move");
          }
          e.preventDefault();
          break;
        default:
          break;
      }
    },
    [playSound]
  );

  // Game loop function
  const runGameLoop = useCallback(() => {
    if (!isPlayingRef.current) return;

    // Update direction
    directionRef.current = nextDirectionRef.current;
    setCurrentDirection(directionRef.current);

    // Calculate new head position
    const head = { ...snakeRef.current[0] };

    switch (directionRef.current) {
      case "up":
        head.y -= gridSize;
        break;
      case "down":
        head.y += gridSize;
        break;
      case "left":
        head.x -= gridSize;
        break;
      case "right":
        head.x += gridSize;
        break;
      default:
        break;
    }

    // Check collision with walls
    if (
      head.x < 0 ||
      head.x >= canvasSizeRef.current.width ||
      head.y < 0 ||
      head.y >= canvasSizeRef.current.height
    ) {
      handleGameOver();
      return;
    }

    // Check collision with self
    const snakeWithoutTail = snakeRef.current.slice(0, -1);
    if (
      snakeWithoutTail.some(
        (segment) => segment.x === head.x && segment.y === head.y
      )
    ) {
      handleGameOver();
      return;
    }

    // Create new snake array with new head
    const newSnake = [head, ...snakeRef.current];

    // Check if food is eaten
    const ateFood =
      head.x === foodRef.current.x && head.y === foodRef.current.y;
    if (ateFood) {
      // Play eat sound
      playSound("eat");

      // Vibrate device on food collection
      vibrateDevice(40);

      // Increase score
      scoreRef.current += 10;
      setScore(scoreRef.current);

      // Place new food with a new random meme image
      placeNewFood();

      // Speed up the game slightly
      if (gameSpeedRef.current > 60) {
        gameSpeedRef.current -= 2;

        // Restart game loop with new speed
        if (gameLoopRef.current) {
          clearInterval(gameLoopRef.current);
          gameLoopRef.current = setInterval(runGameLoop, gameSpeedRef.current);
        }
      }
    } else {
      // Remove tail if no food eaten
      newSnake.pop();
    }

    // Update snake state
    snakeRef.current = newSnake;

    // Draw the updated state
    drawGame();
  }, [drawGame, placeNewFood, handleGameOver, playSound, vibrateDevice]);

  // Start game
  const startGame = useCallback(() => {
    // Reset game state
    snakeRef.current = [
      { x: 3 * gridSize, y: 10 * gridSize },
      { x: 2 * gridSize, y: 10 * gridSize },
      { x: 1 * gridSize, y: 10 * gridSize },
    ];
    directionRef.current = "right";
    nextDirectionRef.current = "right";
    setCurrentDirection("right");
    gameSpeedRef.current = 250;
    scoreRef.current = 0;
    setScore(0);

    // Place initial food
    placeNewFood();

    // Start game
    isPlayingRef.current = true;
    setIsPlaying(true);
    setGameOver(false);

    // Clear any existing interval
    if (gameLoopRef.current) {
      clearInterval(gameLoopRef.current);
    }

    // Force initial draw
    drawGame();

    // Start the game loop with a slight delay
    setTimeout(() => {
      gameLoopRef.current = setInterval(runGameLoop, gameSpeedRef.current);
    }, 100);
  }, [drawGame, placeNewFood, runGameLoop]);

  // Toggle sound
  const toggleSound = useCallback(() => {
    setSoundEnabled((prev) => !prev);
  }, []);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext("2d");
    contextRef.current = context;

    // Set initial canvas size
    resizeCanvas();

    // Place initial food
    placeNewFood();

    // Set up event listeners
    window.addEventListener("resize", resizeCanvas);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      document.removeEventListener("keydown", handleKeyDown);
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
        gameLoopRef.current = null;
      }
    };
  }, [resizeCanvas, placeNewFood, handleKeyDown]);

  // Update isPlayingRef when isPlaying changes
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  const toogleMenu = () => {
    playClick();
    updateUserInfo((prev) => ({
      ...prev,
      isPlay: false,
      currentComponent: task,
      currentComponentText: "task",
      lastComponent: userDetails?.userDetails.currentComponent,
      lastComponentText: userDetails?.userDetails.currentComponentText,
      isMenu: true,
      menuCount: userDetails?.userDetails?.menuCount + 1,
    }));
  };

  // Modify canvas click handler
  const handleCanvasClick = useCallback(
    (e) => {
      if (!isPlayingRef.current && !gameOver) {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Check if click is within start button bounds
        const buttonWidth = 200;
        const buttonHeight = 50;
        const buttonX = (canvas.width - buttonWidth) / 2;
        const buttonY = (canvas.height - buttonHeight) / 2;

        if (
          x >= buttonX &&
          x <= buttonX + buttonWidth &&
          y >= buttonY &&
          y <= buttonY + buttonHeight
        ) {
          startGame();
        }
      }
    },
    [startGame, gameOver]
  );

  // Add useEffect for canvas click listener
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.addEventListener("click", handleCanvasClick);
    return () => canvas.removeEventListener("click", handleCanvasClick);
  }, [handleCanvasClick]);
  console.log();

  return (
    <div className="w-full game-container max-w-4xl mx-auto p-4 bg-gray-900 text-gray-200">
      <img
        onClick={() => {
          toogleMenu(task, "task");
        }}
        src="https://d1ccopf5cw1hft.cloudfront.net/s3data/cancelicon.svg"
        className="cancel-full "
        style={{ cursor: "pointer" }}
      />
      <img
        onClick={() => {
          goToThePage(gameleader, "snake");
        }}
        src={questionMarkIcon}
        alt="Question Mark Icon"
        className="cancel-full-lead"
        style={{ marginLeft: "5px" }}
      />
      <div className="text-center">
        <h4 className="display-6 mb-2 mt-4 text-light   text-center  text-uppercase welcometextmeme">
          Snake Game
        </h4>
      </div>

      <div className="mb-0">
        {/* Score and controls */}
        <div className="row flex">
          <div className="col-8 text-center">
            <div class="bg-gray-800 green text-green-400 px-3 py-2 rounded-full font-bold text-uppercase">
              {IsPointsReached ? "Score" : "Tokens"} : {score}
            </div>
          </div>
        </div>

        {/* Game canvas with touch controls */}
        <div className="relative border1 border-gray-700 rounded bg-gray-800 mb-3 overflow-hidden">
          <canvas
            id="snake-canvas"
            ref={canvasRef}
            className="w-full"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchMove={handleTouchMove}
            tabIndex="0"
          />

          {/* Start Game Overlay */}
          {!isPlaying && !gameOver && (
            <div className="absolute inset-0 flex-snake items-center justify-center bg-black bg-opacity-50">
              <button
                onClick={startGame}
                className="control-btn  hover:bg-blue-700 transition-colors"
              >
                Start Game
              </button>
            </div>
          )}
        </div>

        {/* Direction and game over */}
        <div className="text-center" ref={scrollableRef}>
          {gameOver && (
            <div className="overlay">
              <div className="full-popup">
                <div className="popup-content">
                  <h2 className="epic">Game Over!</h2>
                  <img
                    src="https://d1ccopf5cw1hft.cloudfront.net/s3data/Xicon.png"
                    className="cancel-img"
                    onClick={closePopUp}
                  />
                  {!IsPointsReached ? (
                    <div className="row text-center">
                      <div className="col-12">
                        <div
                          className="epic-div"
                          style={{ padding: "30px 40px" }}
                        >
                          <p className="pointsLabel">TOKENS EARNED</p>
                          <div className="pointsValue1">
                            <img
                              src="https://d1ccopf5cw1hft.cloudfront.net/s3data/coinlogo.png"
                              alt="logo"
                            />
                            <p className="pointsNumber">{score}</p>
                          </div>
                        </div>
                        <p className="bottom-text">
                          Play daily to boost your score and rack up more
                          points!
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="row text-center">
                      <div className="col-12">
                        <div className="epic-div">
                          <p
                            className="pointsLabel"
                            style={{
                              fontSize: "18px",
                              fontWeight: "bold",
                              margin: "20px 0",
                              textAlign: "center",
                            }}
                          >
                            POINTS EARNED: {score} <br />
                            TOKENS EARNED: NIL
                          </p>
                        </div>
                        <p className="bottom-text">
                          Keep slithering! 🐍 Rack up points and chase the high
                          score!
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SnakeGame;
